import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Bike, DollarSign, Clock, Shield, MapPin, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";

export default function DriversLanding() {
  const nav = useNavigate();

  const beneficios = [
    { Icon: Clock, titulo: "Maneja tu tiempo", texto: "Trabaja las horas que quieras. Tú decides cuándo estar disponible." },
    { Icon: DollarSign, titulo: "Ganancias por entrega", texto: "Mínimo $30 MXN por entrega + propinas. Pagos semanales a tu CLABE." },
    { Icon: MapPin, titulo: "Solo tu zona", texto: "Solo ropa y zapatos locales. Rutas cortas, sin cruzar la ciudad." },
    { Icon: Shield, titulo: "Cobertura 90 min", texto: "Entregas rápidas de comercios de barrio a clientes de la misma zona." },
  ];

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1591780980121-3ee54f89ab48?w=1800&q=80"
            alt="Repartidor en moto"
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0F]/70 via-[#0B0B0F]/60 to-[#0B0B0F]" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="overline text-white/70 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20 inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Únete a la red de repartidores · Todo México
            </span>
            <h1 className="mt-6 font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
              Conviértete en un <span className="text-[#D4FF00]">Delivery Hero</span>.
            </h1>
            <p className="mt-6 text-white/85 text-lg lg:text-xl max-w-2xl font-light leading-relaxed">
              Entrega ropa y zapatos locales, <b className="text-white">maneja tu tiempo</b> y <b className="text-white">gana dinero</b> por cada entrega. Rutas cortas, pagos semanales y sin jefes.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button
                data-testid="cta-registrar-repartidor"
                onClick={() => nav("/register?role=repartidor")}
                className="bg-[#FF3366] hover:bg-[#E62958] text-white rounded-sm h-14 px-8 text-base font-display font-bold tracking-tight"
              >
                <Bike className="w-5 h-5 mr-2" /> Registrarme como Repartidor
                <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
              <Link to="/login">
                <Button variant="outline" className="rounded-sm h-14 px-8 border-white/40 bg-white/5 backdrop-blur text-white hover:bg-white/10 hover:text-white text-base">
                  Ya soy repartidor · Entrar
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { n: "$30+", l: "por entrega" },
                { n: "90 min", l: "por ruta" },
                { n: "Semanal", l: "pagos" },
              ].map(s => (
                <div key={s.l}>
                  <div className="font-display font-black text-3xl lg:text-4xl tracking-tighter text-[#D4FF00]">{s.n}</div>
                  <div className="text-xs text-white/60 uppercase tracking-wider mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="overline text-white/50 mb-2">Por qué en 90 MINUTOS</div>
        <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter max-w-2xl">
          Un trabajo hecho para ti, no para el algoritmo.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {beneficios.map((b, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }} viewport={{ once: true }}
              className="bg-white/5 backdrop-blur border border-white/10 rounded-sm p-6 lg:p-8 hover:border-[#D4FF00]/50 transition-colors"
            >
              <b.Icon className="w-8 h-8 text-[#D4FF00]" strokeWidth={2} />
              <div className="mt-4 font-display font-bold text-xl lg:text-2xl tracking-tight">{b.titulo}</div>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{b.texto}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Requisitos + Zona de registro */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 border-t border-white/10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="overline text-white/50 mb-2">Requisitos</div>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter">Todo lo que necesitas para empezar</h2>
            <p className="mt-4 text-white/70 max-w-md">Es rápido: sube tus documentos una sola vez, aprobamos en 24-48h y arrancas.</p>
          </div>
          <ol className="space-y-3 text-sm">
            {[
              "INE o Pasaporte vigente",
              "Licencia de conducir válida",
              "Tarjeta de circulación del vehículo",
              "Comprobante de domicilio reciente (< 3 meses)",
              "Selfie con buena luz",
              "CLABE bancaria de 18 dígitos para pagos",
              "Depósito en garantía de $2,000 MXN — 100% reembolsable",
            ].map((r, i) => (
              <li key={`req-${i}`} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-sm p-4">
                <span className="font-display font-black text-[#D4FF00] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-white/85">{r}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Depósito en garantía callout */}
        <div className="mt-10 bg-[#D4FF00]/10 border border-[#D4FF00]/30 rounded-sm p-6 lg:p-8">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="w-14 h-14 rounded-sm bg-[#D4FF00] grid place-items-center shrink-0">
              <Shield className="w-7 h-7 text-zinc-900" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="overline text-[#D4FF00]">Depósito en garantía · 100% reembolsable</div>
              <h3 className="font-display font-black text-2xl lg:text-3xl tracking-tighter mt-1">
                $2,000 MXN <span className="text-white/70 font-normal text-base">para arrancar</span>
              </h3>
              <p className="mt-3 text-sm text-white/80 max-w-2xl leading-relaxed">
                Es un depósito de seguridad que se libera cuando dejas la plataforma o completas tus primeras 50 entregas. <b className="text-white">No es una comisión ni una cuota</b> — es tu propio dinero guardado como respaldo contra faltantes o mercancía dañada. Se reembolsa automáticamente a tu CLABE registrada.
              </p>
            </div>
          </div>
        </div>

        {/* Zona de registro destacada */}
        <div id="registro" className="mt-10 bg-white text-zinc-900 rounded-sm p-8 lg:p-12 text-center">
          <div className="overline text-[#FF3366] mb-2">Zona de registro</div>
          <h3 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
            Empieza tu registro como <span className="text-[#FF3366]">Delivery Hero</span>
          </h3>
          <p className="mt-4 text-zinc-600 max-w-xl mx-auto">
            Aprobación en 24-48h · Comienza a facturar en menos de una semana · Cero costo de suscripción
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              data-testid="cta-registrar-repartidor-bottom"
              onClick={() => nav("/register?role=repartidor")}
              className="bg-[#FF3366] hover:bg-[#E62958] text-white rounded-sm h-14 px-10 text-base font-display font-bold tracking-tight"
            >
              <Bike className="w-5 h-5 mr-2" /> Registrarme ahora <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
            <Link to="/login">
              <Button variant="outline" className="rounded-sm h-14 px-8 border-zinc-300 text-base">
                Ya soy repartidor · Entrar
              </Button>
            </Link>
          </div>
          <div className="mt-6 text-xs text-zinc-500">
            Al registrarte aceptas los <a href="#" className="underline">Términos</a> y el depósito en garantía reembolsable de $2,000 MXN.
          </div>
        </div>
      </section>
    </div>
  );
}
